import { decamelize, decamelizeKeys } from 'humps';
import {
  ApiResponse,
  ApiSearchResponseModel,
  Competition,
  CompetitionOrganiser,
  PaginationRequestModel,
} from '../models';
import http from '../utils/http';

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey =
  'admin-backend-auth-tokens';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey,
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function getBackend(
  backendName: string,
): BackendAuthToken {
  const backends =
    getBackendAuthTokens();

  const backend = backends.find(
    (item) =>
      item.name === backendName,
  );

  if (!backend) {
    throw new Error(
      `Backend "${backendName}" was not found in authenticated backends`,
    );
  }

  return backend;
}

function getBackendUrl(
  backend: BackendAuthToken,
  path: string,
): string {
  return `${backend.url.replace(/\/$/, '')}${path}`;
}

function getBackendRequestConfig(
  backendName: string,
  extra: any = {},
) {
  return {
    ...extra,
    backend: backendName,
  };
}

/**
 * COMPETITIONS
 *
 * Fetch from all authenticated backends.
 */
export async function serviceFetchCompetitions(
  params: FetchCompetitionsRequest,
): Promise<ApiSearchResponseModel<Competition[]>> {
  const requestParams = {
    ...params,
  };

  if (requestParams.orderBy) {
    requestParams.orderBy =
      decamelize(requestParams.orderBy);
  }

  const decReqData: {
    [key: string]: any;
  } = decamelizeKeys(requestParams);

  const urlParams =
    new URLSearchParams(
      decReqData,
    ).toString();

  const backends =
    getBackendAuthTokens();

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response =
          await http.get(
            getBackendUrl(
              backend,
              `/api/events/competition/search?${urlParams}`,
            ),
            getBackendRequestConfig(
              backend.name,
            ),
          );

        const responseData =
          response?.data ?? {};

        const data = Array.isArray(
          responseData,
        )
          ? responseData
          : responseData?.data ?? [];

        const rowcount =
          responseData?.rowcount ??
          responseData?.rowCount ??
          data.length;

        return {
          data: Array.isArray(data)
            ? data.map(
                (competition: any) => ({
                  ...competition,
                  backendName:
                    backend.name,
                  backendUrl:
                    backend.url,
                }),
              )
            : [],
          rowcount,
        };
      } catch {
        return {
          data: [],
          rowcount: 0,
        };
      }
    }),
  );

  const competitions: any[] = [];
  let totalRowcount = 0;

  responses.forEach(
    (response) => {
      competitions.push(
        ...response.data,
      );

      totalRowcount +=
        response.rowcount;
    },
  );

  return {
    data: competitions,
    rowcount: totalRowcount,
  };
}

export interface FetchCompetitionsRequest
  extends PaginationRequestModel {
  id?: number;
  name?: string;
  idCompetitionOrganiser?: string;
  isLive?: boolean;
  hasOrder?: boolean;
  hasRestrictions?: boolean;
  idCompetitionTypes?: number[];
  idCompetition?: number;
}

export async function serviceGetCompetition(
  id: number,
  backendName: string,
): Promise<Competition> {
  const backend =
    getBackend(backendName);

  const response:
    ApiResponse<Competition> =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/competition/id/${id}`,
      ),
      getBackendRequestConfig(
        backendName,
      ),
    );

  return {
    ...response.data,
    backendName,
    backendUrl: backend.url,
  } as Competition;
}

export async function serviceUpdateCompetition(
  id: number,
  values: any,
  backendName: string,
): Promise<Competition> {
  const backend =
    getBackend(backendName);

  const response =
    await http.patch(
      getBackendUrl(
        backend,
        `/api/events/competition/edit/${id}`,
      ),
      values,
      getBackendRequestConfig(
        backendName,
        {
          headers: {
            'Content-Type':
              'multipart/form-data',
            successAlert: true,
          },
        },
      ),
    );

  return {
    ...response.data,
    backendName,
    backendUrl: backend.url,
  } as Competition;
}

/**
 * COMPETITION ORGANISERS
 *
 * Fetch from all authenticated backends.
 */
export async function serviceFetchCompetitionOrganisers(
  params: FetchCompetitionsRequest,
): Promise<
  ApiSearchResponseModel<CompetitionOrganiser[]>
> {
  const requestedPage = Math.max(
    Number(params.page) || 0,
    0,
  );

  const pageSize = Math.max(
    Number(params.limit) || 50,
    1,
  );

  const isPagination =
    params.isPagination !== false;

  const effectiveOrderBy =
    params.orderBy || 'name';

  const isDescending =
    String(params.direction).toLowerCase() ===
    'desc';

  const requestParams = {
    ...params,
    ...(isPagination
      ? {
          page: 0,
          limit:
            (requestedPage + 1) *
            pageSize,
        }
      : {}),
  };

  if (requestParams.orderBy) {
    requestParams.orderBy =
      decamelize(requestParams.orderBy);
  }

  const decReqData: {
    [key: string]: any;
  } = decamelizeKeys(requestParams);

  const urlParams =
    new URLSearchParams(
      decReqData,
    ).toString();

  const backends =
    getBackendAuthTokens();

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response =
          await http.get(
            getBackendUrl(
              backend,
              `/api/events/competition-organiser/search?${urlParams}`,
            ),
            getBackendRequestConfig(
              backend.name,
            ),
          );

        const responseData =
          response as unknown as {
            data?: any[];
            rowcount?: number;
            rowCount?: number;
          };

        const data = Array.isArray(
          responseData.data,
        )
          ? responseData.data
          : [];

        const parsedRowcount = Number(
          responseData.rowcount ??
            responseData.rowCount,
        );

        const rowcount = Number.isFinite(
          parsedRowcount,
        )
          ? parsedRowcount
          : data.length;

        return {
          data: Array.isArray(data)
            ? data.map(
                (organiser: any) => ({
                  ...organiser,
                  backendName:
                    backend.name,
                  backendUrl:
                    backend.url,
                }),
              )
            : [],
          rowcount,
        };
      } catch {
        return {
          data: [],
          rowcount: 0,
        };
      }
    }),
  );

  const organisers: any[] = [];
  let totalRowcount = 0;

  responses.forEach(
    (response) => {
      organisers.push(
        ...response.data,
      );

      totalRowcount +=
        response.rowcount;
    },
  );

  organisers.sort((first, second) => {
    const firstValue =
      first[effectiveOrderBy as keyof typeof first];
    const secondValue =
      second[effectiveOrderBy as keyof typeof second];

    let comparison = 0;

    if (
      typeof firstValue === 'number' &&
      typeof secondValue === 'number'
    ) {
      comparison = firstValue - secondValue;
    } else {
      comparison = String(
        firstValue ?? '',
      ).localeCompare(
        String(secondValue ?? ''),
        undefined,
        {
          sensitivity: 'base',
          numeric: true,
        },
      );
    }

    if (comparison === 0) {
      comparison = String(
        first.backendName ?? '',
      ).localeCompare(
        String(second.backendName ?? ''),
      );
    }

    if (comparison === 0) {
      comparison = first.id - second.id;
    }

    return isDescending
      ? -comparison
      : comparison;
  });

  const start =
    requestedPage * pageSize;

  const paginatedOrganisers = isPagination
    ? organisers.slice(
        start,
        start + pageSize,
      )
    : organisers;

  return {
    data: paginatedOrganisers,
    rowcount: totalRowcount,
  };
}

export async function serviceSearchCompetitionOrganisers(
  name: string,
): Promise<CompetitionOrganiser[]> {
  const backends =
    getBackendAuthTokens();

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response:
          ApiResponse<
            CompetitionOrganiser[]
          > = await http.get(
            getBackendUrl(
              backend,
              `/api/events/competition-organiser/search?&name=${encodeURIComponent(name)}`,
            ),
            getBackendRequestConfig(
              backend.name,
            ),
          );

        const data =
          Array.isArray(response.data)
            ? response.data
            : [];

        return data.map(
          (organiser: any) => ({
            ...organiser,
            backendName:
              backend.name,
            backendUrl:
              backend.url,
          }),
        );
      } catch {
        return [];
      }
    }),
  );

  return responses.flat();
}

export async function serviceUpdateCompetitionOrganiser(
  id: number,
  values: FormData,
  backendName: string,
): Promise<any> {
  const backend =
    getBackend(backendName);

  const response =
    await http.patch(
      getBackendUrl(
        backend,
        `/api/events/competition-organiser/edit/${id}`,
      ),
      values,
      getBackendRequestConfig(
        backendName,
        {
          headers: {
            'Content-Type':
              'multipart/form-data',
          },
        },
      ),
    );

  return response.data;
}

/**
 * COMPETITION CONFIGURATION
 *
 * This belongs to one competition/backend,
 * so backendName is required.
 */
export async function getCompetitionConfigurationService(
  idCompetition: number,
  backendName: string,
): Promise<any> {
  const backend =
    getBackend(backendName);

  const response:
    ApiResponse<any[]> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/quota/management/percentage/all',
      ),
      getBackendRequestConfig(
        backendName,
        {
          params: {
            idCompetition,
          },
        },
      ),
    );

  return response.data;
}

export async function deleteCompetitionConfigurationService(
  idQuotaManagement: number,
  backendName: string,
): Promise<any> {
  const backend =
    getBackend(backendName);

  const response =
    await http.delete(
      getBackendUrl(
        backend,
        '/api/events/quota/management/percentage',
      ),
      getBackendRequestConfig(
        backendName,
        {
          params: {
            idQuotaManagement,
          },
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

export async function applyLeagueConfiguration(
  idCompetition: number,
  backendName: string,
): Promise<any> {
  const backend =
    getBackend(backendName);

  const response =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/quotas/config/generate',
      ),
      {
        idCompetition,
      },
      getBackendRequestConfig(
        backendName,
      ),
    );

  return response.data;
}
